import { appType } from "@/shared/lib/appType";

// 이 어댑터를 경유해 등록된 host 누적 집합 — native updateDynamic 은 dynamicHosts 를 "교체"하므로
// (append 아님), 서로 다른 소비자(우편번호 / 향후 PG)가 각자 push 하면 서로를 덮어쓴다.
// 여기서 합집합을 유지해 매 push 마다 전체를 전달하고, postMessage 성공 경로에서만 커밋한다.
// 보장 범위: "이 런타임에서 이 어댑터로 등록된 값"만 — 기존 Keychain·외부 등록값은 병합하지 않는다(비목표).
let registeredUrls = new Set<string>();

/**
 * WebView URL 동적 allowlist 에 host 를 등록한다 (BRIDGE_PROTOCOL §1.5, iOS 전용).
 *
 * iOS 는 allowlist 미일치 http(s) 도 WebView 안에서 로드한다(외부 위임은 비-http 스킴만). 이 push 는 native 동적
 * allowlist 계약(§1.5)을 유지하는 best-effort 이며 embed 로드의 전제조건이 아니다 — 계속 둘지는 네이티브와 재확인.
 * AOS 는 http(s) 자유 항행이라 iOS 에서만 동작한다.
 *
 * - native sanitize 는 scheme=https + host 를 요구하고 그 외(bare host·http·비-http 스킴)는 무음 드롭 →
 *   반드시 "https://<host>" 절대 URL 로 전달한다.
 * - updateDynamic 이 dynamicHosts 를 교체하므로 이 어댑터 경유 등록분의 합집합을 항상 push.
 *   불변식: 모든 allowlist push 는 이 함수를 경유해야 소비자 간 상호 덮어쓰기가 없다.
 * - 핸들러 미주입이면 등록 상태를 커밋하지 않고 반환 → 다음 호출이 있으면 재시도 가능.
 * - postMessage 성공 경로에서만 registeredUrls 커밋 → best-effort 실패 시 재시도 여지 +
 *   StrictMode 이중 mount 안전(성공 후 재호출은 변경 없음으로 스킵).
 */
export const registerAllowedUrls = (urls: string[]): void => {
  if (appType() !== "iOS") return;

  const handler = window.webkit?.messageHandlers?.allowedUrlsHandler;
  if (!handler) return; // 미주입 — 커밋/스킵하지 않고 다음 호출에서 재시도 여지를 남긴다

  const next = new Set(registeredUrls);
  for (const url of urls) next.add(url);
  if (next.size === registeredUrls.size) return; // 변경 없음 → 스킵 (StrictMode 이중 호출 흡수)

  try {
    handler.postMessage({ allowedUrls: [...next] });
    registeredUrls = next; // 성공 경로에서만 등록 상태 커밋
  } catch {
    // best-effort — 실패 시 registeredUrls 미갱신이라 다음 호출에서 재시도 가능
  }
};

/**
 * 카카오(구 다음) 우편번호 embed iframe host — .embed() 시 로드되는 문서 호스트.
 * API 전역은 window.daum.Postcode, SDK 는 t1.daumcdn.net 이지만 embed 문서는
 * postcode.map.kakao.com 에서 서빙된다(카카오 도메인 이전 — 옛 postcode.map.daum.net 은 미사용).
 * 항행 게이트에 걸리는 건 이 iframe 문서 host 뿐이다(정적 리소스 host 는 항행 아님).
 */
export const KAKAO_POSTCODE_ALLOWED_URLS = ["https://postcode.map.kakao.com"];

/** 앱 부팅 시 1회 — native 동적 allowlist 계약을 유지하려고 우편번호 iframe host 를 best-effort 로 등록한다. */
export const registerKakaoPostcodeAllowlist = (): void => {
  registerAllowedUrls(KAKAO_POSTCODE_ALLOWED_URLS);
};
